import * as React from "react";
import styled from "@emotion/styled";
import { ImgBackground } from "./FeaturedImage";
import type { Image } from "../types";

type ReviewableBannerProps = Omit<
  React.ComponentProps<typeof BannerOrFallbackImg>,
  "src" | "isFallbackSrc"
> & {
  src?: Image;
  fallback: string | Image;
};

const createImgAttributes = (src: string | Image) => {
  if (typeof src === "string") {
    return { src };
  }

  let srcSet: string | undefined;
  if (src.url600 || src.url1200) {
    srcSet = [
      src.url600 ? `${src.url600} 600w` : null,
      src.url1200 ? `${src.url1200} 1200w` : null,
    ]
      .filter(Boolean)
      .join(", ");
  }

  return {
    src: src.url1200 ?? src.url,
    srcSet,
    sizes: srcSet ? "100vw" : undefined,
  };
};

/**
 * Banner image component for ReviewableTemplate
 */
function ReviewableBanner({
  src,
  fallback,
  className,
  ...props
}: ReviewableBannerProps) {
  const isFallbackSrc = !src;

  // Build <img> attributes depending on `src` type
  const attrs = createImgAttributes(isFallbackSrc ? fallback : src);

  let blur: string | undefined;
  if (isFallbackSrc && typeof fallback === "object") {
    blur = fallback.blur;
  } else {
    blur = src && src.blur;
  }

  return (
    <BannerOrFallbackImgBackground
      blur={blur}
      isFallbackSrc={isFallbackSrc}
      className={className}
    >
      <BannerOrFallbackImg
        {...attrs}
        {...props}
        isFallbackSrc={isFallbackSrc}
      />
    </BannerOrFallbackImgBackground>
  );
}

const BannerOrFallbackImg = styled.img<{
  isFallbackSrc: boolean;
}>(({ isFallbackSrc }) => ({
  aspectRatio: "auto !important",
  display: "block",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  position: "relative",
  transformOrigin: "center",
  width: "100%",
  ...(isFallbackSrc && {
    filter: "blur(5vw) brightness(1.1)",
    transform: "scale(-2, 2) translate(10%, 0)",
  }),
}));

const BannerOrFallbackImgBackground = styled(ImgBackground, {
  shouldForwardProp: (prop) => prop !== "isFallbackSrc",
})<{
  isFallbackSrc: boolean;
}>(({ isFallbackSrc }) => ({
  maxHeight: 320,

  ...(isFallbackSrc
    ? {
        "&:before": {
          filter: "blur(5vw) brightness(1.1)",
          transform: "scale(-2, 2) translate(10%, 0)",
          transformOrigin: "center",
        },
      }
    : {}),
}));

export { ReviewableBanner };
